"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { isA2UIEnvelopeMessage, parseDataModel } from "@/lib/utils/a2uiCanvasPayload";
import { setPathValue, valuesEqual, walkPath } from "./binding";
import { renderMacaronComponent } from "./registry";
import type { RecordAny, SurfaceComponent } from "./types";
import { getMacaronCardSurfaceStyle, MACARON_CARD_SURFACE_CLASSNAME } from "./cardSurface";

const SELF_CONTAINED_SURFACE_COMPONENTS = new Set(["Card", "RollPickerCard"]);
const CANVAS_SHADOW_BLEED_CLASSNAME = "w-full min-w-0 -mx-2 px-2";

interface MacaronCanvasRendererProps {
  payload?: Record<string, unknown>;
  messages?: Record<string, unknown>[];
  onAction?: (
    actionName: string,
    sourceComponentId: string,
    context?: Record<string, unknown> | null,
    dataModel?: Record<string, unknown>,
  ) => void;
  onLocalHandler?: (
    operation: string,
    sourceComponentId: string,
    value?: unknown,
  ) => void;
}

function extractEnvelopeList(payload?: RecordAny, messages?: RecordAny[]): RecordAny[] {
  if (Array.isArray(messages) && messages.length > 0) {
    return messages;
  }
  if (Array.isArray(payload?.objects) && payload.objects.length > 0) {
    return payload.objects as RecordAny[];
  }
  if (Array.isArray(payload?.messages) && payload.messages.length > 0) {
    return payload.messages as RecordAny[];
  }
  return [];
}

function parseSurfaceFromEnvelopes(envelopes: RecordAny[]): { root: string; components: SurfaceComponent[] } {
  const begin = [...envelopes].reverse().find((item) => item.beginRendering) as { beginRendering?: RecordAny } | undefined;
  const root = typeof begin?.beginRendering?.root === "string" ? begin.beginRendering.root : "root";
  const componentMap = new Map<string, SurfaceComponent>();
  for (const envelope of envelopes) {
    const components = Array.isArray((envelope.surfaceUpdate as RecordAny | undefined)?.components) ? (((envelope.surfaceUpdate as RecordAny | undefined)?.components as SurfaceComponent[])) : [];
    for (const component of components) {
      if (typeof component?.id !== "string" || !component.id) continue;
      componentMap.set(component.id, component);
    }
  }
  return { root, components: Array.from(componentMap.values()) };
}

function parseSurfaceFromPayloadOnly(payload?: RecordAny): { root: string; components: SurfaceComponent[] } {
  const objects = Array.isArray(payload?.objects) ? (payload?.objects as RecordAny[]) : [];
  if (objects.length === 0) {
    return { root: "root", components: [] };
  }
  return parseSurfaceFromEnvelopes(objects.filter(isA2UIEnvelopeMessage));
}

function resolveSupportWidgetPath(expr: unknown): string | undefined {
  if (!expr || typeof expr !== "object" || typeof (expr as RecordAny).path !== "string") return undefined;
  const path = ((expr as RecordAny).path as string).trim();
  return path || undefined;
}

function resolveSupportWidgetBoolean(expr: unknown, data: RecordAny): boolean {
  if (!expr || typeof expr !== "object") return false;
  const record = expr as RecordAny;
  if (typeof record.path === "string") {
    const current = walkPath(record.path, data);
    if (typeof current === "boolean") return current;
  }
  if (typeof record.literalBool === "boolean") return record.literalBool;
  if (typeof record.literalBoolean === "boolean") return record.literalBoolean;
  return false;
}

function getBooleanAnyOfUpdates(components: SurfaceComponent[], data: RecordAny): Array<{ path: string; value: boolean }> {
  const updates: Array<{ path: string; value: boolean }> = [];
  for (const component of components) {
    if (!component?.component || typeof component.component !== "object") continue;
    const entry = Object.entries(component.component)[0];
    if (!entry || entry[0] !== "BooleanAnyOf") continue;
    const config = (entry[1] || {}) as RecordAny;
    const outputPath = resolveSupportWidgetPath(config.output);
    if (!outputPath) continue;
    const inputs = (Array.isArray(config.inputs) ? config.inputs : []).filter(
      (input): input is RecordAny => Boolean(input && typeof input === "object" && resolveSupportWidgetPath(input) !== outputPath),
    );
    updates.push({ path: outputPath, value: inputs.some((input) => resolveSupportWidgetBoolean(input, data)) });
  }
  return updates;
}

export default function MacaronCanvasRenderer({ payload, messages, onAction, onLocalHandler }: MacaronCanvasRendererProps) {
  const envelopes = useMemo(() => extractEnvelopeList(payload as RecordAny, messages as RecordAny[] | undefined), [payload, messages]);

  const { root, components } = useMemo(() => {
    if (envelopes.length > 0) {
      return parseSurfaceFromEnvelopes(envelopes);
    }
    return parseSurfaceFromPayloadOnly(payload as RecordAny);
  }, [envelopes, payload]);

  const parsedData = useMemo(() => {
    if (envelopes.length > 0) {
      return parseDataModel(envelopes);
    }
    const objects = Array.isArray((payload as RecordAny)?.objects) ? ((payload as RecordAny).objects as RecordAny[]) : [];
    return parseDataModel(objects.filter(isA2UIEnvelopeMessage));
  }, [envelopes, payload]);
  const [data, setData] = useState<RecordAny>(() => parsedData);
  const [lastParsedData, setLastParsedData] = useState<RecordAny>(parsedData);
  if (!valuesEqual(lastParsedData, parsedData)) {
    setLastParsedData(parsedData);
    setData(parsedData);
  }
  // dataRef lets click handlers that fire updateData + onAction in the same tick (PasswordKeypad, ActionSelectionList) see the freshly committed data; React state is async and the closure value would otherwise lag one render.
  const dataRef = useRef<RecordAny>(data);
  dataRef.current = data;
  useEffect(() => {
    const updates = getBooleanAnyOfUpdates(components, data);
    if (updates.length === 0) return;
    setData((current) => {
      let next = current;
      for (const update of updates) {
        if (valuesEqual(walkPath(update.path, next), update.value)) continue;
        next = setPathValue(next, update.path, update.value);
      }
      return next;
    });
  }, [components, data]);
  const componentMap = useMemo(() => new Map(components.map((item) => [item.id, item])), [components]);
  const hasExplicitCardSurface = useMemo(
    () =>
      components.some((item) => {
        if (!item?.component || typeof item.component !== "object") return false;
        // Some widgets render their own full card shell even if the payload does not include a raw `Card` node.
        return Object.keys(item.component).some((type) => SELF_CONTAINED_SURFACE_COMPONENTS.has(type));
      }),
    [components],
  );
  const getComponentEntry = (id: string) => {
    const node = componentMap.get(id);
    if (!node || !node.component || typeof node.component !== "object") return undefined;
    const entry = Object.entries(node.component)[0];
    if (!entry) return undefined;
    const [type, configRaw] = entry;
    return { type, config: (configRaw || {}) as RecordAny, weight: typeof node.weight === "number" ? node.weight : undefined };
  };

  const renderById = (id: string): React.ReactNode => {
    const entry = getComponentEntry(id);
    if (!entry) return null;
    return renderMacaronComponent(entry.type, entry.config, {
      data,
      updateData: (path, value) => {
        if (!path.trim()) return;
        const currentValue = walkPath(path, dataRef.current);
        if (valuesEqual(currentValue, value)) return;
        const next = setPathValue(dataRef.current, path, value);
        dataRef.current = next;
        setData(next);
      },
      renderChild: renderById,
      getComponentType: (childId) => getComponentEntry(childId)?.type,
      getComponentConfig: (childId) => getComponentEntry(childId)?.config,
      getComponentWeight: (childId) => getComponentEntry(childId)?.weight,
      onAction: (actionName, sourceId, context) => {
        onAction?.(actionName, sourceId, context, dataRef.current);
      },
      onLocalHandler: (operation, sourceId, value) => {
        onLocalHandler?.(operation, sourceId, value);
      },
      componentId: id,
    });
  };

  const renderedRoot = renderById(root);
  if (!renderedRoot) return null;

  if (!hasExplicitCardSurface) {
    // Some A2UI payloads are just rows/columns/labels with no explicit Card nodes.
    // In chat they still need the same visual separation as card-based payloads, or the whole surface reads like plain prose.
    return (
      <div className={CANVAS_SHADOW_BLEED_CLASSNAME}>
        <div data-slot="macaron-canvas-surface-fallback-card" className={MACARON_CARD_SURFACE_CLASSNAME} style={getMacaronCardSurfaceStyle()}>
          {renderedRoot}
        </div>
      </div>
    );
  }

  return <div className={CANVAS_SHADOW_BLEED_CLASSNAME}>{renderedRoot}</div>;
}
